/**
 * Company Configuration for YovoTrip (Client-Side)
 * 
 * Public-facing company details for use in:
 * - Footer components
 * - Contact pages
 * - Policy pages
 * 
 * NOTE: This is the client-side version. The server-side version
 * (server/config/company.js) is the source of truth for policy
 * versions and acceptance text. Keep these in sync.
 */

// ══════════════════════════════════════════════════════════════════════════════
// POLICY VERSIONS (must match server/config/company.js)
// ══════════════════════════════════════════════════════════════════════════════

export const POLICY_VERSIONS = {
  termsOfService: '1.0.0',
  privacyPolicy: '1.0.0',
  cancellationRefundPolicy: '1.0.0',
};

// ══════════════════════════════════════════════════════════════════════════════
// POLICY ROUTES
// ══════════════════════════════════════════════════════════════════════════════

export const POLICY_ROUTES = {
  userAgreement: '/user-agreement',           // Primary legal route
  termsOfService: '/terms-of-service',        // Backward compatible alias
  privacyPolicy: '/privacy-policy',
  cancellationRefundPolicy: '/cancellation-refund-policy',
};

// ══════════════════════════════════════════════════════════════════════════════
// COMPANY DETAILS (Public-facing only)
// No personal phone numbers, no sensitive internal details
// ══════════════════════════════════════════════════════════════════════════════

export const COMPANY = {
  // Brand
  brandName: 'YovoTrip',
  tagline: 'Your Journey, Our Passion',
  
  // Legal Entity (required for compliance)
  legalEntity: {
    name: 'Yovotrip Private Limited',
    brandNames: 'YovoTrip / Yovo',
    cin: 'U79110MH2025PTC456478',
    gstin: '29AACCY0780A1ZH',
    registeredOffice: '4th Floor, 28/A, Unit-FO-02, Smart Avenue, Michael Palaya, Indiranagar, Bengaluru, Karnataka - 560038',
    jurisdiction: 'Bengaluru, Karnataka',
  },
  
  // Contact (role-based emails and support details)
  contact: {
    supportEmail: 'support@yovotrip.com',
    supportPhone: '+91-8796330087',
    supportHours: '9:00 AM to 6:00 PM (excluding Sundays and Public Holidays)',
    grievanceEmail: 'grievance@yovotrip.com',
    infoEmail: 'info@yovotrip.com',
    privacyEmail: 'privacy@yovotrip.com',
  },
  
  // Grievance Officer (required for compliance)
  grievanceOfficer: {
    name: 'Aryan Srivastva',
    designation: 'Director / Grievance Officer',
    email: 'grievance@yovotrip.com',
    phone: '9315499284',
    address: '4th Floor, 28/A, Unit-FO-02, Smart Avenue, Michael Palaya, Indiranagar, Bengaluru, Karnataka - 560038',
    acknowledgementTimeline: 'Within 48 hours',
    resolutionTimeline: 'Within 5-15 days, subject to complexity, supplier dependency, investigation, and applicable law',
  },
  
  // Privacy Contact / Data Protection Contact
  privacyContact: {
    name: 'Ravikiran Srinivas Maganti',
    designation: 'Director',
    email: 'privacy@yovotrip.com',
    phone: '8652081835',
    address: '4th Floor, 28/A, Unit-FO-02, Smart Avenue, Michael Palaya, Indiranagar, Bengaluru, Karnataka - 560038',
  },
  
  // Social media links
  social: {
    facebook: 'https://www.facebook.com/share/1CzTS3wxwj/',
    twitter: 'https://x.com/YovoTrip',
    instagram: 'https://www.instagram.com/yovotrip',
    linkedin: 'https://www.linkedin.com/company/yovo-trip/',
    youtube: 'https://www.youtube.com/@yovotrip'
  },
  
  // Website
  website: 'https://www.yovotrip.com',
};

// ══════════════════════════════════════════════════════════════════════════════
// SEO DEFAULTS (PageHelmet — aligned with sitemap.config.js siteUrl)
// ══════════════════════════════════════════════════════════════════════════════

const SITE_BASE = COMPANY.website.replace(/\/+$/, '');

export const SEO_DEFAULTS = {
  siteName: COMPANY.brandName,
  defaultTitle: `${COMPANY.brandName} — Cheap Flights, Hotels & Holiday Packages | Book Online`,
  defaultDescription:
    'Book cheap flights, hotels, and holiday packages on YovoTrip. Compare airline fares, find the best deals, and enjoy hassle-free travel booking across India and worldwide.',
  defaultOgImage: `${SITE_BASE}/logo512.png`,
  twitterSite: '@YovoTrip',
  baseUrl: SITE_BASE,
};

// ══════════════════════════════════════════════════════════════════════════════
// FOOTER LINKS CONFIGURATION
// ══════════════════════════════════════════════════════════════════════════════

export const FOOTER_LEGAL_LINKS = [
  { label: 'Privacy Policy', path: POLICY_ROUTES.privacyPolicy },
  { label: 'User Agreement / Terms of Use', path: POLICY_ROUTES.userAgreement },
  { label: 'Cancellation & Refund Policy', path: POLICY_ROUTES.cancellationRefundPolicy },
];

export const FOOTER_SUPPORT_LINKS = [
  { label: 'Contact Us', path: '/contact' },
  { label: 'FAQs', path: '/faqs' },
  { label: 'Sitemap', path: '/sitemap' },
  { label: 'Grievance Redressal', path: '/contact#grievance' },
  { label: 'My Trips', path: '/my-trips' },
];

// ══════════════════════════════════════════════════════════════════════════════
// ACCEPTANCE TEXT (for UI display - server provides authoritative snapshot)
// ══════════════════════════════════════════════════════════════════════════════

export const ACCEPTANCE_TEXT = {
  getStandardText() {
    return `I have read and agree to YovoTrip's User Agreement / Terms of Use, Privacy Policy, and Cancellation & Refund Policy.`;
  },
  getSignupText() {
    return `I agree to YovoTrip's User Agreement / Terms of Use and acknowledge the Privacy Policy.`;
  },
  insuranceConsent: 'I consent to share my personal information with the insurance provider for policy issuance and claims processing.',
};

export default {
  POLICY_VERSIONS,
  POLICY_ROUTES,
  COMPANY,
  SEO_DEFAULTS,
  FOOTER_LEGAL_LINKS,
  FOOTER_SUPPORT_LINKS,
  ACCEPTANCE_TEXT,
};
